import { FC, useEffect, useRef } from 'react';
import { select } from 'd3-selection';
import type { ScaleBand, ScaleLinear } from 'd3-scale';

type BarData = {
  label: string;
  value: number;
  color: string;
};

interface BarProps {
  xScale: ScaleBand<string>;
  yScale: ScaleLinear<number, number>;
  data: BarData[];
  height: number;
}

const Bar: FC<BarProps> = ({ xScale, yScale, data, height }) => {
  const ref = useRef<SVGRectElement>(null);

  const barTransition = () => {
    const node = ref.current;
    if (node) {
      select(node)
        .selectAll('.bar')
        .data(data)
        .transition()
        .duration(1000)
        .attr('y', d => yScale(d.value))
        .attr('height', d => height - yScale(d.value));
    }
  };

  const init = () => {
    if (ref.current) {
      // prepare initial data from where transition starts.
      const initialData = data.map(obj => ({
        label: obj.label,
        value: obj.value,
        color: obj.color
      }));
      // prepare the field
      const bar = select(ref.current).selectAll('.bar').data(initialData);

      // bar outline total
      bar
        .enter()
        .append('rect')
        .attr('classlabel', 'background-bar')
        .attr('x', d => xScale(d.label) ?? 0)
        .attr('y', 0)
        .attr('rx', 100)
        .attr('ry', 10)
        .attr('dy', 0)
        .attr('width', 20)
        .attr('height', height)
        .attr('stroke', d => d.color)
        .attr('style', 'fill: none; stroke-width: 1;');
      // bar fill
      bar
        .enter()
        .append('rect')
        .attr('class', 'bar')
        .attr('x', d => xScale(d.label) ?? 0)
        .attr('y', height)
        .attr('rx', 100)
        .attr('ry', 10)
        .attr('dy', 0)
        .attr('width', 20)
        .attr('height', 0)
        .attr('fill', d => d.color);
      // bar label
      bar
        .enter()
        .append('text')
        .attr('class', 'amount')
        .attr('x', d => xScale(d.label) ?? 0)
        .attr('y', height)
        .attr('dx', 10)
        .attr('dy', 12)
        .attr('text-anchor', 'middle')
        .attr('style', 'font-size: .8rem; color: yellow; font-weight: 200;')
        .text(d => d.label);
      // bar value
      bar
        .enter()
        .append('text')
        .attr('class', 'amount')
        .attr('x', d => xScale(d.label) ?? 0)
        .attr('y', height)
        .attr('dx', 10)
        .attr('dy', 30)
        .attr('text-anchor', 'middle')
        .attr('style', 'font-size: 1.25rem; font-weight: 500;')
        .text(d => d.value.toString() ?? '');
      barTransition();
    }
  };

  useEffect(() => {
    init();
  }, [data]);

  return <g className="bar-group" ref={ref} />;
};
export default Bar;
